import { Alert, Box, Card, CardContent, CardMedia, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { SelectedParametersSimpleType, UserType, VehicleGridType, AlertType } from "../../../../types/types";
import {
    BadgeOutlined,
    FeedOutlined,
    FingerprintOutlined,
    GpsFixedOutlined,
    GridOnOutlined,
    InfoOutlined,
    LocalPhoneOutlined,
    LocationCityOutlined,
    ManageHistoryOutlined,
    ManOutlined,
    NearMeOutlined,
    NotificationImportantOutlined,
    PictureAsPdfOutlined,
    PlaceOutlined,
    SendToMobileOutlined,
    Settings,
    SpeedOutlined,
    WhatsApp
} from "@mui/icons-material";
import { phoneNumberFormatter, setToastRefToOpen, formattedSpeed, getTimezone, getVehicleIgnitionStatus } from "../../../../functions/functions";
import findAllLastTemPositionsExport from "../../../../resources/Api/vehicle/findAllLastTemPositionsExport";
import { MenuItem as MenuIt } from "primereact/menuitem";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { selectedCustomerToManagerMapAtom, userAtom } from "../../../../atoms/atoms";
import { Toast } from "primereact/toast";
import dayjs from "dayjs";

import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { isImpersonationLocked } from "../../../../utils/impersonation";

dayjs.extend(utc);
dayjs.extend(timezone);

const items: MenuIt[] = [
    {
        label: 'PDF',
        icon: <PictureAsPdfOutlined />,
    },
    {
        label: 'Excel',
        icon: <GridOnOutlined />
    },
    {
        label: "Excel simples",
        icon: <GridOnOutlined />,
    },
];

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const now = Date.now();

const getLastPositionBadge = (date?: string) => {
    if (!date) {
        return { text: "sem dados", color: "#9e9e9e" };
    }

    const nowTime = dayjs();
    const time = dayjs(date);

    const diffMinutes = nowTime.diff(time, "minute");
    const diffHours = nowTime.diff(time, "hour");
    const diffDays = nowTime.diff(time, "day");

    if (diffHours < 1) {
        return {
            text: `há ${diffMinutes} minutos`,
            color: "#2e7d32",
        };
    }

    if (diffHours < 24) {
        return {
            text: `há ${diffHours} horas`,
            color: "#2e7d32",
        };
    }

    if (diffHours < 48) {
        return {
            text: `há ${diffHours} horas`,
            color: "#ed6c02",
        };
    }

    return {
        text: `há ${diffDays} dias`,
        color: "#d32f2f",
    };
};

const PopUpContent = ({
    selected,
    setActions,
    setValue,
    withLivingTracking = false,
    setTracking,
    onFocusNearby
}: {
    selected: VehicleGridType & { isWhatsApp: boolean, attributes: any },
    setActions: () => void,
    setValue: (value: number) => void,
    withLivingTracking?: boolean,
    setTracking?: any,
    onFocusNearby?: () => void
}) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    const selectedCustomerToManagerMap = useRecoilValue<SelectedParametersSimpleType>(
        selectedCustomerToManagerMapAtom
    );

    // functionalities states
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);

    // states
    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<any>(null);

    // refs
    const toastModalRef = useRef<Toast>(null);

    // cookies
    const [cookies] = useCookies([
        "selectedParameters",
        "token",
    ]);

    // hooks
    const location = useLocation();

    const onOpenStreetViewer = useCallback(async (lat: number, lng: number) => {
        const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat}%2C${lng}&heading=${0}`;
        window.open(url, "_blank");
    }, []);

    const lastFixTime = useMemo(() => selected?.position?.zonedFixTime
        ? new Date(selected.position.zonedFixTime).getTime()
        : null, [selected?.position?.zonedFixTime]);

    const isWithin24h = useMemo(() => lastFixTime !== null && now - lastFixTime <= TWENTY_FOUR_HOURS, [lastFixTime]);
    const lastPositionBadge = useMemo(() => getLastPositionBadge(selected?.position?.zonedFixTime ?? undefined), [selected?.position?.zonedFixTime]);

    const isImpersonating = isImpersonationLocked();
    const ignitionStatus = getVehicleIgnitionStatus(selected?.attributes, selected?.position?.protocol, selected?.idle);

    useEffect(() => {
        const ref = toastModalRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible
    ]);

    return (
        <>
            <Toast
                ref={toastModalRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />

            <Card
                className="vehicle-popup-card !tw-overflow-hidden !tw-rounded-xl !tw-bg-white tw-shadow-xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth dark:!tw-bg-slate-900"
                sx={{
                    width: { xs: "min(360px, calc(100vw - 48px))", sm: 420 },
                    maxWidth: "100%",
                    fontFamily: "Prometo",
                    border: "1px solid rgba(0, 108, 119, 0.14)",
                    "& .vehicle-popup-details > .MuiCardContent-root": {
                        px: 1.75,
                        py: 1,
                    },
                    "& .vehicle-popup-details > .MuiCardContent-root:last-child": {
                        pb: 1,
                    },
                    "& .vehicle-popup-details > .MuiCardContent-root .MuiBox-root": {
                        alignItems: "flex-start",
                        gap: 0.75,
                        py: 0.5,
                    },
                    "& .vehicle-popup-details > .MuiCardContent-root .MuiBox-root > .MuiSvgIcon-root, & .vehicle-popup-details > .vehicle-popup-information-row > .MuiSvgIcon-root": {
                        color: "#006C77",
                        flexShrink: 0,
                        fontSize: 20,
                        mt: 0.15,
                    },
                    "& .vehicle-popup-details > .vehicle-popup-information-row": {
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        px: 1.75,
                        py: 1,
                    },
                    "& .MuiTypography-body1": {
                        color: "#263238",
                        fontFamily: "Prometo",
                        fontSize: { xs: "0.78rem", sm: "0.84rem" },
                        lineHeight: 1.45,
                        overflowWrap: "anywhere",
                    },
                    "& .MuiDivider-root": {
                        borderColor: "rgba(0, 108, 119, 0.12)",
                    },
                }}
            >
                <Box
                    className="vehicle-popup-header tw-relative tw-flex tw-items-center tw-justify-center tw-bg-slate-50 dark:tw-bg-slate-950"
                    sx={{ minHeight: { xs: 82, sm: 92 } }}
                >
                    <CardMedia
                        image={require("../../../../assets/files/header3.png")}
                        sx={{ height: { xs: 82, sm: 92 }, width: "100%", objectFit: "cover" }}
                        aria-hidden="true"
                    />
                    <Box
                        className="status-color"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.75,
                            top: "10px !important",
                            right: "auto !important",
                            left: "12px !important",
                        }}
                    >
                        <Tooltip title={ignitionStatus.label} arrow>
                            <Box
                                role="img"
                                aria-label={ignitionStatus.label}
                                tabIndex={0}
                                style={{
                                    height: 22,
                                    width: 22,
                                    borderRadius: 100,
                                    position: "relative",
                                    left: "unset",
                                    top: "unset",
                                    cursor: "pointer",
                                    margin: 0,
                                    border: "3px solid #fff",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                                    background: ignitionStatus.color,
                                }}
                            />
                        </Tooltip>
                        {selected?.hasAlerts && (
                            <Tooltip title={selected.alerts.map((a: AlertType) => a.description).join(", ")}>
                                <NotificationImportantOutlined
                                    sx={{
                                        color: "#d32f2f",
                                        cursor: "pointer",
                                        fontSize: 26,
                                        animation: "pulse 1.5s infinite"
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                <Box
                    className="vehicle-popup-actions tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-gradient-to-r tw-from-gtr-teal-dark tw-to-gtr-teal"
                    sx={{
                        minHeight: 46,
                        "& .MuiIconButton-root": {
                            color: "#fff",
                            transition: "all 180ms ease",
                        },
                        "& .MuiIconButton-root:not(.Mui-disabled):hover": {
                            bgcolor: "rgba(255, 255, 255, 0.12)",
                            transform: "translateY(-1px)",
                        },
                        "& .MuiSvgIcon-root": { marginRight: "0 !important" },
                    }}
                >
                    <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                        <Tooltip
                            title={"Clique para falar com o motorista via WhatsApp."}
                            style={{ color: "black", margin: 5, cursor: "pointer" }}
                        >
                            <span>
                                <IconButton aria-label="Falar com o motorista pelo WhatsApp" disabled={!selected?.isWhatsApp || isImpersonating} onClick={() => {
                                    window.open(`https://wa.me/${selected.driver.phone}?text=Olá, ${selected.driver.name}`);
                                }}>
                                    <WhatsApp style={{ marginRight: 5, fill: !selected?.isWhatsApp || isImpersonating ? "gray" : "#ffff" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                        <Tooltip
                            title={"Clique para ver informações do veículo selecionado."}
                            style={{ color: "black", margin: 5, cursor: "pointer" }}
                        >
                            <IconButton aria-label="Abrir informações do veículo" onClick={() => setActions()}>
                                <InfoOutlined style={{ marginRight: 5, fill: "#ffff" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                        <Tooltip
                            title={"Clique para ver a localização do veículo na visão de imagem da rua."}
                            style={{ color: "black", margin: 5, cursor: "pointer" }}
                        >
                            <IconButton aria-label="Abrir visão da rua" onClick={() => {
                                onOpenStreetViewer(selected?.position?.lat, selected?.position?.lng)
                            }}>
                                <ManOutlined style={{ marginRight: 5, fill: "#ffff" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box className="vehicle-popup-details">
                    {((!selected?.driver?.name && !selected?.driverUnrecorded) || selected?.driverUnrecorded) && (
                        <CardContent className="vehicle-popup-section vehicle-popup-driver-section">
                            <Box className="vehicle-popup-wide-row" style={{ display: "flex", justifyContent: "", alignItems: "center", margin: 0 }}>
                                <FingerprintOutlined style={{ marginRight: 5 }} />
                                <Typography variant="body1">
                                    <span
                                        style={{
                                            fontWeight: "bold",
                                            fontFamily: "Prometo",
                                        }}
                                    >
                                        Identificador:{" "}
                                    </span>{" "}
                                    {selected?.driverUnrecorded ?? "Sem identificação de motorista (iButton ausente)."}
                                </Typography>
                            </Box>
                        </CardContent>
                    )}

                    {selected?.driver?.name && (
                        <CardContent className="vehicle-popup-section vehicle-popup-driver-section">
                            <Box style={{ display: "flex", alignItems: "center", margin: 0 }}>
                                <FingerprintOutlined style={{ marginRight: 5 }} />
                                <Typography variant="body1">
                                    <span style={{ fontWeight: "bold", fontFamily: "Prometo" }}>
                                        Identificador:{" "}
                                    </span>
                                    {selected?.driver?.driverIdentifier ?? ""}
                                </Typography>
                            </Box>

                            <Divider style={{ background: "gray" }} />
                            <Box style={{ display: "flex", alignItems: "center", margin: 0 }}>
                                <BadgeOutlined style={{ marginRight: 5 }} />
                                <Typography variant="body1">
                                    <span style={{ fontWeight: "bold", fontFamily: "Prometo" }}>
                                        Motorista:{" "}
                                    </span>
                                    {selected?.driver?.name ?? ""}
                                </Typography>
                            </Box>

                            <Divider style={{ background: "gray" }} />
                            <Box style={{ display: "flex", alignItems: "center", margin: 0 }}>
                                <LocationCityOutlined style={{ marginRight: 5 }} />
                                <Typography variant="body1">
                                    <span style={{ fontWeight: "bold", fontFamily: "Prometo" }}>
                                        Departamento:{" "}
                                    </span>
                                    {selected?.driver?.driverDepartmentReducedName ?? ""}
                                </Typography>
                            </Box>

                            <Divider style={{ background: "gray" }} />
                            <Box style={{ display: "flex", alignItems: "center", margin: 0 }}>
                                <LocalPhoneOutlined style={{ marginRight: 5 }} />
                                <Typography variant="body1">
                                    <span style={{ fontWeight: "bold", fontFamily: "Prometo" }}>
                                        Telefone:{" "}
                                    </span>
                                    {phoneNumberFormatter(selected?.driver?.phone ?? "")}
                                </Typography>
                            </Box>
                        </CardContent>
                    )}

                    <Divider style={{ background: "gray" }} />
                    {getCurrentUser.configs.isManager && selected?.zonedSatelliteLastUpdate && (
                        <Box className="vehicle-popup-information-row vehicle-popup-communication-row">
                            <ManageHistoryOutlined style={{ marginRight: 5 }} />
                            <Typography variant="body1">
                                <span style={{ fontWeight: "bold" }}>Última comunicação servidor satelital: </span>{" "}
                                {selected?.zonedSatelliteLastUpdate ? `${dayjs(selected?.zonedSatelliteLastUpdate).format(
                                    "DD/MM/YYYY"
                                )} às ${dayjs(selected?.zonedSatelliteLastUpdate).format(
                                    "HH:mm:ss"
                                )}` : "-"}
                            </Typography>
                        </Box>
                    )}

                    <Divider style={{ background: "gray" }} />
                    {getCurrentUser.configs.isManager && (
                        <Box className="vehicle-popup-information-row vehicle-popup-communication-row">
                            <ManageHistoryOutlined style={{ marginRight: 5 }} />
                            <Typography variant="body1">
                                <span style={{ fontWeight: "bold" }}>Última comunicação servidor GPRS: </span>{" "}
                                {selected?.zonedDeviceLastUpdate ? `${dayjs(selected?.zonedDeviceLastUpdate).format(
                                    "DD/MM/YYYY"
                                )} às ${dayjs(selected?.zonedDeviceLastUpdate).format(
                                    "HH:mm:ss"
                                )}` : "-"}
                            </Typography>
                        </Box>
                    )}

                    <Divider style={{ background: "gray" }} />
                    <Box className="vehicle-popup-information-row vehicle-popup-wide-row">
                        <ManageHistoryOutlined style={{ marginRight: 5 }} />
                        <Typography variant="body1">
                            <span style={{ fontWeight: "bold" }}>Última posição enviada: </span>{" "}
                            {`${dayjs(selected.position.zonedFixTime).tz(getTimezone(selected.utcZone ?? "")).format(
                                "DD/MM/YYYY"
                            )} às ${dayjs(selected.position.zonedFixTime).tz(getTimezone(selected.utcZone ?? "")).format(
                                "HH:mm:ss"
                            )} - ${(selected.position.protocol === "teltonika" || selected.position.protocol === "rst" || selected.position.protocol === "teltonikamqtt") ? "GPRS" : selected.position.protocol}`}
                        </Typography>
                    </Box>

                    <Divider style={{ background: "gray" }} />
                    <Box className="vehicle-popup-information-row vehicle-popup-wide-row">
                        <PlaceOutlined style={{ marginRight: 5 }} />
                        <Typography variant="body1">
                            <span style={{ fontWeight: "bold", fontFamily: "Prometo" }}>
                                Endereço:{" "}
                            </span>{" "}
                            {selected?.position?.address ?? `${selected?.position?.lat}, ${selected?.position?.lng}`}
                        </Typography>
                    </Box>

                    <Divider style={{ background: "gray" }} />
                    <Box className="vehicle-popup-information-row vehicle-popup-wide-row">
                        <SpeedOutlined style={{ marginRight: 5 }} />
                        <Typography variant="body1">
                            <span style={{ fontWeight: "bold", fontFamily: "Prometo" }}>
                                Velocidade:{" "}
                            </span>{" "}
                            {`${formattedSpeed(selected?.position?.speed)}`}
                        </Typography>
                    </Box>

                    {!isWithin24h && (
                        <>
                            <Divider style={{ background: "gray" }} />
                            <Box className="vehicle-popup-information-row vehicle-popup-wide-row vehicle-popup-alert-row">
                                <Alert
                                    style={{ borderRadius: 5, padding: 1 }}
                                    severity="warning"
                                >
                                    {`Os dados acima representam a posição adquirida na última comunicação desse dispositivo, o mesmo não comunica com o servidor ${lastPositionBadge.text}`}
                                </Alert>
                            </Box>
                        </>
                    )}

                    <Divider style={{ background: "gray" }} />
                </Box>
                <Box
                    className="vehicle-popup-actions tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-gradient-to-r tw-from-gtr-teal-dark tw-to-gtr-teal"
                    sx={{
                        minHeight: 46,
                        "& .MuiIconButton-root": {
                            color: "#fff",
                            transition: "all 180ms ease",
                        },
                        "& .MuiIconButton-root:not(.Mui-disabled):hover": {
                            bgcolor: "rgba(255, 255, 255, 0.12)",
                            transform: "translateY(-1px)",
                        },
                        "& .MuiSvgIcon-root": { marginRight: "0 !important" },
                    }}
                >
                    <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                        <Tooltip
                            placement="top"
                            title={"Clique para imprimir um relatório sobre as ultimas posições do veículo."}
                            style={{ color: "black", margin: 5, cursor: "pointer" }}
                        >
                            <Box>
                                <IconButton
                                    aria-label="Exportar últimas posições"
                                    ref={(ref) => anchorRef.current = ref}
                                    className="refresh-button-icon"
                                    onClick={() => setOpen(true)}
                                    sx={{
                                        background: "transparent",
                                        transition: "all 0.2s ease-out allow-discrete",
                                        marginRight: "3px",
                                    }}
                                >
                                    <FeedOutlined style={{ color: "#ffff" }} />
                                </IconButton>
                                <Menu
                                    open={open}
                                    anchorEl={() => anchorRef.current as HTMLElement}
                                    onClose={() => { setOpen(false) }}
                                    slotProps={{
                                        paper: {
                                            className: "!tw-rounded-xl tw-shadow-xl",
                                            sx: { mt: 0.75, minWidth: 150 },
                                        },
                                    }}
                                >
                                    {items.map((option: MenuIt) => (
                                        <MenuItem key={option?.id} onClick={async () => {
                                            setOpen(false);
                                            const customerId = getCurrentUser.configs.isManager ? selectedCustomerToManagerMap.customerId : cookies.selectedParameters.customer.id;
                                            const departmentId = getCurrentUser.configs.isManager ? selectedCustomerToManagerMap.departmentId : cookies.selectedParameters.department.id;
                                            const isViewingAll = getCurrentUser.configs.isManager ? selectedCustomerToManagerMap?.isViewingAll : cookies.selectedParameters?.isViewingAll;

                                            let reportType = option.label!.toLocaleLowerCase();
                                            let isSimpleType = false;
                                            if (reportType.includes("simple")) {
                                                reportType = reportType.split(" ")[0];
                                                isSimpleType = true;
                                            }

                                            const { data } = await findAllLastTemPositionsExport(
                                                cookies.token, customerId,
                                                departmentId, selected.id,
                                                reportType,
                                                isViewingAll,
                                                isSimpleType
                                            );

                                            if (data.byteLength === 0) {
                                                setTextValidation("Nenhum dado para mostrar no relatório.");
                                                setStatusMessage("warn");
                                                setFieldsIsValid(false);
                                                return;
                                            }

                                            const typeExport = (option.label!.toLocaleLowerCase() === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                                            const file = new Blob([data], { type: typeExport });
                                            const fileURL = URL.createObjectURL(file);
                                            window.open(fileURL);
                                        }}>
                                            {` ${option.label}`}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        </Tooltip>
                    </Box>

                    <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                        <Tooltip
                            title={"Clique para enviar um comando para o veículo."}
                            style={{ color: "black", margin: 5, cursor: "pointer" }}
                        >
                            <span>
                                <IconButton aria-label="Enviar comando ao veículo" disabled={!location?.state?.commands?.canView || isImpersonating} onClick={() => {
                                    setActions();
                                    setValue(2);
                                }}>
                                    <SendToMobileOutlined style={{ marginRight: 5, fill: !location?.state?.commands?.canView || isImpersonating ? "gray" : "#ffff" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                        <Tooltip
                            title={"Clique para personalizar o envio de comandos automáticos para o veículo."}
                            style={{ color: "black", margin: 5, cursor: "pointer" }}
                        >
                            <span>
                                <IconButton aria-label="Configurar comandos automáticos" disabled={!getCurrentUser.configs.customerSettings?.sendAutomaticCommands || isImpersonating} onClick={() => {
                                    setActions();
                                    setValue(3);
                                }}>
                                    <Settings style={{ marginRight: 5, fill: !getCurrentUser.configs.customerSettings?.sendAutomaticCommands || isImpersonating ? "gray" : "#ffff" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    {withLivingTracking && (
                        <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                            <Tooltip
                                title={"Clique para seguir veículo selecionado."}
                                style={{ color: "black", margin: 5, cursor: "pointer" }}
                            >
                                <IconButton aria-label="Seguir veículo" disabled={false} onClick={() => {
                                    if (setTracking) setTracking(true);
                                }}>
                                    <GpsFixedOutlined style={{ marginRight: 5, fill: "#ffff" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {onFocusNearby && (
                        <Box style={{ display: "flex", justifyContent: "center", flex: "1 1 0", minWidth: 0 }}>
                            <Tooltip
                                title="Clique para focar nos veículos próximos e ver o trajeto até o mais próximo."
                                style={{ color: "black", margin: 5, cursor: "pointer" }}
                            >
                                <IconButton aria-label="Focar veículos próximos" onClick={() => onFocusNearby()}>
                                    <NearMeOutlined style={{ marginRight: 5, fill: "#ffff" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Card>
        </>
    );
};

export default PopUpContent;
